/**
 * Canny generation completion — SSE push (webhook-driven).
 *
 * Renders nothing. Opens a per-user EventSource to `/api/editor-agent/gen-stream`,
 * which forwards WaveSpeed-webhook completion events (see
 * `src/lib/queue/canny-gen-events.ts`). On a completion it applies the result
 * to the store through the SAME `applyCompletedGenJob` helper the fallback
 * poller uses — one idempotency guard, so an event delivered by BOTH the push
 * and the (now-slow) poll can't double-add a clip.
 *
 * Mounted once in `EditorShell` next to `useGenerationPoller` so it survives the
 * chat sidebar unmounting when `leftMode` switches away from `'chat'`.
 */
'use client';

import { useEffect, useRef } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import {
  applyCompletedGenJob,
  failCompletedGenJob,
} from '@/lib/editor/agent/applyCompletedGenJob';

export function useCannyGenStream(): null {
  const { getToken } = useAuthToken();
  // toolCallId -> first-shot aspect already applied (shared shape with the poller).
  const aspectAppliedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Auth: this app stores the JWT in localStorage (only Google login sets a
    // cookie), and EventSource can't send headers — so pass it as `?token=`.
    // `withCredentials` still forwards the cookie when a login flow set one.
    const token = getToken();
    const url = token
      ? `/api/editor-agent/gen-stream?token=${encodeURIComponent(token)}`
      : '/api/editor-agent/gen-stream';
    const es = new EventSource(url, { withCredentials: true });

    es.onmessage = (event) => {
      try {
        const d = JSON.parse(event.data) as {
          taskId?: string;
          status?: string;
          src?: string;
          videoId?: string;
          error?: string;
        };
        if (!d.taskId) return;
        if (d.status === 'completed') {
          applyCompletedGenJob(d.taskId, { src: d.src, videoId: d.videoId }, aspectAppliedRef.current);
        } else if (d.status === 'failed') {
          failCompletedGenJob(d.taskId, d.error || 'Generation failed');
        }
      } catch {
        // keepalive / non-JSON — ignore.
      }
    };

    // EventSource auto-reconnects on transient errors; leave it open. The
    // fallback poller (useGenerationPoller) recovers anything missed while down.
    es.onerror = () => {};

    return () => es.close();
    // `getToken` is stable (useCallback []), so this opens one long-lived stream.
  }, [getToken]);

  return null;
}
